import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Button } from '@deepguide-ai/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@deepguide-ai/ui/dialog'
import { toast } from 'sonner'
import { queryClient, trpc } from '@/lib/trpc'
import { vendorLogoPath } from '@/lib/vendor-logo'
import { openOAuthConnectPopup } from '../oauth-connect-lifecycle'
import type { ConfigModalProps, IntegrationProvider } from '../types'

function CheckrConfigModal({
  open,
  onOpenChange,
  integration,
  onComplete,
  workspaceId,
}: ConfigModalProps) {
  const [connecting, setConnecting] = useState(false)
  const disconnect = useMutation(
    trpc.integrations.deleteIntegration.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.backgroundCheck.listProviders.queryKey(),
        })
        onComplete()
        onOpenChange(false)
      },
    })
  )
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-md'>
        <DialogHeader>
          <DialogTitle>Checkr</DialogTitle>
          <DialogDescription>
            Connect an existing Checkr account as its administrator. Collect
            dashboard access and screening evidence, and link existing reports
            to employees in People.
          </DialogDescription>
        </DialogHeader>
        <p className='text-muted-foreground text-sm'>
          Checkr grants account-level access through its partner app. Screenata
          uses this connection to read existing data. Local disconnect retains
          collected reports; revoke app access in Checkr separately.
        </p>
        {disconnect.error && (
          <p role='alert' className='text-destructive text-sm'>
            {disconnect.error.message}
          </p>
        )}
        <DialogFooter className='flex-wrap'>
          {integration && (
            <Button
              variant='destructive'
              disabled={connecting || disconnect.isPending}
              onClick={() => disconnect.mutate({ id: integration.id })}
            >
              {disconnect.isPending ? 'Disconnecting…' : 'Disconnect'}
            </Button>
          )}
          <Button
            variant='outline'
            disabled={connecting || disconnect.isPending}
            onClick={() => {
              setConnecting(true)
              void CheckrProvider.handleConnect({
                workspaceId,
                onSuccess: onComplete,
                onFinish: () => setConnecting(false),
              }).catch(() => {})
            }}
          >
            {connecting ? 'Connecting…' : integration ? 'Reconnect' : 'Connect'}
          </Button>
          {integration && (
            <Button asChild>
              <Link
                to='/workspaces/$workspaceId/people'
                params={{ workspaceId }}
                onClick={() => onOpenChange(false)}
              >
                Open People
              </Link>
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export const CheckrProvider: IntegrationProvider = {
  id: 'Checkr',
  name: 'Checkr',
  description: 'Background screening evidence and dashboard account access',
  Icon: () => <img src={vendorLogoPath('checkr')} alt='' className='size-6' />,
  ConfigModal: CheckrConfigModal,
  postAuthAction: 'CONFIGURE',
  getFeatures: () => [
    {
      id: 'hr_evidence',
      name: 'Screening evidence',
      description:
        'Link existing candidate reports and inspect screening status in People',
      enabled: true,
    },
  ],
  handleConnect: async ({ onSuccess, onFinish }) => {
    try {
      const result = await queryClient
        .getMutationCache()
        .build(queryClient, trpc.integrations.initCheckrAuth.mutationOptions())
        .execute(undefined)
      openOAuthConnectPopup({
        authUrl: result.authUrl,
        windowName: 'checkr-auth',
        provider: 'Checkr',
        onSuccess,
        onFinish,
        onError: (message) => toast.error(message),
        onPopupBlocked: () => toast.error('Allow popups to connect Checkr.'),
      })
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : 'Could not start Checkr authorization'
      )
      onFinish?.('error')
      throw error
    }
  },
  onConnectComplete: async ({ code, state }) => {
    const result = await queryClient
      .getMutationCache()
      .build(
        queryClient,
        trpc.integrations.completeCheckrAuth.mutationOptions()
      )
      .execute({ code, state })
    await queryClient.invalidateQueries({
      queryKey: trpc.backgroundCheck.listProviders.queryKey(),
    })
    return result
  },
  syncData: async () => {
    throw new Error(
      'Use Scan for account checks, or sync a linked report from People.'
    )
  },
}
